import DomainModel from "../../domain-model";
import omitDefaults from "../../json/omit-defaults";
import Signal from "../../control-flow/signal";

interface Frame {
    /*
     * A unique identifier for this frame. If multiple frames are added with
     * the same name, it relpaces the previous frame. If the name is null, then
     * it will never be merged with previous frames.
     *
     * This name can often be omitted, but it's helpful for collapsing multiple
     * serial changes to a single property. So if a user twiddles the width
     * of something twelve times, the undo/redo stack records only the first
     * and last values of the twiddling session rather than twelve intermediate
     * steps. If the user changes the width, then the height, and the width
     * again, all those actions remain distinct and individually undo/redoable.
     */
    name?: string;

    /**
     * A string that describes the result of the action currently being
     * comitted to the history. If this changes a property and the new value is
     * ten, the description should read "set value to ten".
     */
    description: string;

    state: string;
}

type Ctor<T> = { fromJSON(state: object): T };

/**
 * The model history is a simplification of the function-based undo stacks in
 * CommandHistory and UndoManager.
 */
export class ModelHistory<T extends DomainModel> {

    private ctor: Ctor<T>;

    /** The undo stack contains most of the history. */
    private undoStack: Frame[] = [];
    /** After undoing, the frames are stored temporarily on the redo stack. */
    private redoStack: Frame[] = [];

    onUndo = new Signal<[T]>('onUndo', 'model-history');
    onRedo = new Signal<[T]>('onRedo', 'model-history');

    onChange = new Signal('onChange', 'model-history');

    constructor(ctor: Ctor<T>, initial: T) {
        this.ctor = ctor;

        this.undoStack.push({
            name: null,
            description: 'Initial state',
            state: JSON.stringify(initial, omitDefaults, 0),
        });

        this.onUndo.add(this.onChange.trigger);
        this.onRedo.add(this.onChange.trigger);
    }

    add(name: string | null, description: string, model: T) {
        // empty the redo stack
        this.redoStack.length = 0;

        const last = this.undoStack[this.undoStack.length];
        if(last && name != null && last.name === name) {
            this.undoStack.pop();
        }
        this.undoStack.push({
            name,
            description,
            state: JSON.stringify(model, omitDefaults, 0),
        });
        this.onChange.trigger();
    }

    undo = () => {
        if(this.undoStack.length > 1) {
            // Move the last frame to the redo stack
            this.redoStack.push(this.undoStack.pop());
            // Restore the frame _before_ that
            const { state } = this.undoStack[this.undoStack.length - 1];
            const model = this.ctor.fromJSON(JSON.parse(state));
            this.onUndo.trigger(model);
        }
    }

    redo = () => {
        if(this.redoStack.length > 0) {
            const frame = this.redoStack.pop();
            this.undoStack.push(frame);
            const model = this.ctor.fromJSON(JSON.parse(frame.state));
            this.onRedo.trigger(model);
        }
    }

    /**
     * If nothing has been added to the history, or if we've already undone all
     * of the things in the history, then we're unable to undo anymore. This
     * method is useful for setting the enabled or disabled state of the undo
     * button.
     * @returns Whether or not calling undo will have any effect.
     */
    canUndo(): boolean {
        return this.undoStack.length > 1;
    }

    /**
     * This method is useful for setting the enabled or disabled state of the
     * undo button. The only times that the redo method will have an effect is
     * basically just after we have undone something.
     * @return Whether or not calling redo will have any effect.
     */
    canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    /**
     * Get a tool tip for the undo button that shows what action will be
     * undone.
     */
    getUndoDescription(): string {
        if(this.undoStack.length > 1) {
            return "Undo " + this.undoStack[this.undoStack.length - 1].description;
        } else {
            return "Nothing to undo";
        }
    }

    /**
     * Get a tool tip for the redo button that shows what action will be
     * redone.
     */
    getRedoDescription(): string {
        if(this.redoStack.length > 0) {
            return "Redo " + this.redoStack[this.redoStack.length - 1].description;
        } else {
            return "Nothing to redo";
        }
    }

}
export default ModelHistory;
